/**
 * Shoppable video - Splide-based reel carousel.
 *
 * Ported from the reference "shoppable-video" section (Splide.js v4). Defines
 * two custom elements:
 *  - <viewport-media>: lazy-loads a <template> video/iframe when it enters
 *    the viewport, and wires up play/pause + mute buttons + a progress bar.
 *  - <splide-carousel>: wraps a `.splide` element with the Splide.js engine
 *    (assets/splide-vendor.js, loaded before this file) and auto plays the
 *    active slide's video while pausing the rest.
 */
(function () {
  'use strict';

  if (!customElements.get('viewport-media')) {
    class ViewportMedia extends HTMLElement {
      constructor() {
        super();
        this.initObserver();
      }

      initObserver() {
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.loadContent();
                  this.observer.unobserve(entry.target);
                  this.observer.disconnect();
                }
              });
            },
            { rootMargin: '200px 0px 200px 0px' }
          );

          this.observer.observe(this);
        } else {
          this.loadContent();
        }
      }

      loadContent() {
        if (this.getAttribute('loaded')) return;
        const template = this.querySelector('template');
        if (!template || !template.content || !template.content.firstElementChild) return;

        const content = document.createElement('div');
        content.appendChild(template.content.firstElementChild.cloneNode(true));

        this.setAttribute('loaded', true);
        const deferredElement = this.appendChild(content.querySelector('video, iframe'));

        if (deferredElement.nodeName === 'IFRAME') {
          const src = deferredElement.getAttribute('data-src');
          if (src) deferredElement.setAttribute('src', src);
          deferredElement.onload = () => this.removePlaceholder();
        }

        if (deferredElement.nodeName === 'VIDEO') {
          const isAutoplay = deferredElement.getAttribute('autoplay');
          const poster = deferredElement.getAttribute('poster');
          if (!isAutoplay && poster) deferredElement.setAttribute('poster', poster);

          deferredElement.onloadeddata = () => {
            this.removePlaceholder();
            if (isAutoplay) {
              deferredElement.muted = true;
              deferredElement.play().catch(function () {});
            }
            this.initVideoControls(deferredElement);
          };
          deferredElement.load();
        }
      }

      initVideoControls(video) {
        const playPauseButton = this.querySelector('.video-play-pause');
        const muteToggleButton = this.querySelector('.video-mute-toggle');
        const progressFilled = this.querySelector('.video-progress-filled');
        let updateProgress;

        if (playPauseButton) {
          playPauseButton.addEventListener('click', function () {
            if (video.paused) {
              video.play();
              playPauseButton.classList.remove('is-play');
              playPauseButton.classList.add('is-pause');
              updateProgress = requestAnimationFrame(updateProgressBar);
            } else {
              video.pause();
              playPauseButton.classList.remove('is-pause');
              playPauseButton.classList.add('is-play');
              cancelAnimationFrame(updateProgress);
            }
          });

          video.addEventListener('ended', function () {
            playPauseButton.classList.remove('is-pause');
            playPauseButton.classList.add('is-play');
          });
        }

        if (muteToggleButton) {
          muteToggleButton.addEventListener('click', function () {
            video.muted = !video.muted;
            if (video.muted) {
              muteToggleButton.classList.add('is-muted');
              muteToggleButton.classList.remove('is-unmuted');
            } else {
              muteToggleButton.classList.remove('is-muted');
              muteToggleButton.classList.add('is-unmuted');
            }
          });
        }

        if (progressFilled) {
          var updateProgressBar = function () {
            const percentage = (video.currentTime / video.duration) * 100 || 0;
            progressFilled.style.width = percentage + '%';
            updateProgress = requestAnimationFrame(updateProgressBar);
          };

          video.addEventListener('timeupdate', updateProgressBar);

          video.addEventListener('ended', function () {
            cancelAnimationFrame(updateProgress);
            progressFilled.style.width = '0%';
          });

          video.addEventListener('canplay', function () {
            progressFilled.style.width = '0%';
            if (!video.paused) updateProgress = requestAnimationFrame(updateProgressBar);
          });
        }
      }

      removePlaceholder() {
        const placeholder = this.querySelector('.video-placeholder');
        if (placeholder) placeholder.style.display = 'none';
      }
    }

    customElements.define('viewport-media', ViewportMedia);
  }

  if (!customElements.get('splide-carousel')) {
    class SplideCarousel extends HTMLElement {
      constructor() {
        super();
        this.slideInstance = null;
      }

      connectedCallback() {
        if (this.slideInstance) return;

        const splideElement = this.querySelector('.splide');
        const options = splideElement && splideElement.getAttribute('data-splide');

        if (options) {
          try {
            const parsedOptions = JSON.parse(options);
            this.slideInstance = new Splide(splideElement, parsedOptions);

            this.slideInstance.on('mounted', () => this.handleVideoPlayback());
            this.slideInstance.on('moved', () => this.handleVideoPlayback());
            setTimeout(() => this.handleVideoPlayback(), 100);
            setTimeout(() => this.initializeProgressBar(), 300);
            this.slideInstance.mount();
          } catch (e) {
            console.error('Failed to initialize Splide carousel:', e);
          }
        }

        this.initObserver();

        if (window.Shopify && Shopify.designMode) {
          this.addShopifyEventListeners();
        }
      }

      disconnectedCallback() {
        if (window.Shopify && Shopify.designMode) {
          this.removeShopifyEventListeners();
        }
      }

      initObserver() {
        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.handleVideoPlayback();
              });
            },
            { rootMargin: '0px 0px -50% 0px' }
          );

          this.observer.observe(this);
        } else {
          this.handleVideoPlayback();
        }
      }

      handleVideoPlayback() {
        if (!this.slideInstance) return;
        if (this.hasAttribute('data-play-videos') && this.getAttribute('data-play-videos') === 'true') return;

        const slides = this.slideInstance.Components.Elements.slides;

        slides.forEach((slide, index) => {
          const video = slide.querySelector('.xo-shoppable-video__video');

          if (video && !slide.classList.contains('is-clone')) {
            const isActiveSlide = this.slideInstance.index === index;

            if (isActiveSlide) {
              if (video.paused) {
                video.play().catch(function (e) {
                  console.error('Error playing video:', e);
                });
              }
            } else if (!video.paused) {
              video.pause();
            }
          } else if (video) {
            video.pause();
          }
        });
      }

      addShopifyEventListeners() {
        this.handleBlockSelect = this.handleBlockSelect.bind(this);
        this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
        document.addEventListener('shopify:block:select', this.handleBlockSelect);
        document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      removeShopifyEventListeners() {
        document.removeEventListener('shopify:block:select', this.handleBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
      }

      handleBlockSelect(event) {
        const blockElement = event.target;
        const carouselElement = blockElement.closest('splide-carousel');
        if (carouselElement === this && this.slideInstance) {
          const index = parseInt(blockElement.getAttribute('data-splide-index'), 10);
          if (!isNaN(index)) {
            this.slideInstance.go(index);
            this.slideInstance.Components.Autoplay.pause();
          }
        }
      }

      handleBlockDeselect() {}

      initializeProgressBar() {
        const bar = this.querySelector('.xo-shoppable-video__progress-fill');
        if (bar && this.slideInstance) {
          const updateProgressBar = () => {
            requestAnimationFrame(() => {
              const end = this.slideInstance.Components.Controller.getEnd() + 1;
              const rate = Math.min((this.slideInstance.index + 1) / end, 1);
              bar.style.width = 100 * rate + '%';
            });
          };

          updateProgressBar();
          this.slideInstance.on('mounted move', updateProgressBar);
          this.slideInstance.on('destroy', () => {
            this.slideInstance.off('mounted move', updateProgressBar);
          });
        }
      }
    }

    customElements.define('splide-carousel', SplideCarousel);
  }
})();
